import { cva } from '@/styled-system/css';

export const dateInputRecipe = cva({
  base: {
    color: 'text.neutral',
    backgroundColor: {
      base: 'DateInput.bg.default',
    },
    cursor: 'text',
    textStyle: 'body',
    _placeholder: {
      color: 'DateInput.text.placeholder',
      textTransform: 'uppercase',
    },
    _autofill: {
      WebkitTextFillColor: 'text.neutral',
      boxShadow: {
        base: 'inset 0 0 0 {borderWidths.default} var(--shadow-color), inset 0 0 0 1000px {colors.DateInput.bg.default}',
      },
    },
    transition: {
      _motionReduce: 'none',
      _motionSafe: 'box-shadow 200ms ease, background-color 200ms ease',
    },
  },
  variants: {
    disabled: {
      true: {
        opacity: 'disabled',
        cursor: 'default',
      },
      false: {
        'backgroundColor': {
          _hover: 'DateInput.bg.hover',
          _focus: 'bg.neutral',
          _active: 'bg.neutral',
        },
        'boxShadow': {
          _focusVisible: 'inset 0 0 0 {borderWidths.default} var(--shadow-color), {shadows.focus}',
        },
        'boxShadowColor': {
          _hover: 'DateInput.border.hover',
          _focus: 'border.action.hero.selected.default',
          _active: 'border.action.hero.selected.default',
        },
        '&.edge-autofilled, &:-webkit-autofill, &:autofill': {
          WebkitTextFillColor: 'text.neutral',
          color: 'text.neutral',
          boxShadow: {
            _hover:
              'inset 0 0 0 {borderWidths.Input.default} var(--shadow-color), inset 0 0 0 1000px {colors.Input.bg.hover}',
            _focus:
              'inset 0 0 0 {borderWidths.Input.default} var(--shadow-color), {shadows.focus}, inset 0 0 0 1000px {colors.Input.bg.active}',
            _active:
              'inset 0 0 0 {borderWidths.Input.default} var(--shadow-color), inset 0 0 0 1000px {colors.Input.bg.active}',
          },
        },
        '&:is(:hover:active), &:is(:hover:focus)': {
          backgroundColor: 'bg.neutral',
        },
      },
    },
    error: {
      true: {},
      false: {},
    },
    fluid: {
      true: {
        width: '100%',
      },
      false: {
        width: 'auto',
      },
    },
    readOnly: {
      true: {
        backgroundColor: {
          base: 'Input.bg.readOnly',
        },
        boxShadowColor: 'transparent.invisible',
      },
      false: {},
    },
    size: {
      sm: {
        borderRadius: 'obj.default',
        paddingBlock: 'DateInput.sm.padding.vert',
        paddingInline: 'default',
      },
      md: {
        borderRadius: 'obj.default',
        paddingBlock: 'DateInput.md.padding.vert',
        paddingInline: 'default',
      },
      lg: {
        borderRadius: 'obj.default',
        paddingBlock: 'default',
        paddingInline: 'default',
      },
    },
    hasContext: {
      true: {},
      false: {
        boxShadow: {
          base: 'inset 0 0 0 {borderWidths.default} var(--shadow-color)',
          _autofill:
            'inset 0 0 0 {borderWidths.default} var(--shadow-color), inset 0 0 0 1000px {colors.DateInput.bg.autofill}',
        },
        boxShadowColor: {
          base: 'DateInput.border.default',
        },
      },
    },
  },
  compoundVariants: [
    {
      readOnly: false,
      hasContext: false,
      css: {
        backgroundColor: { base: 'DateInput.bg.default' },
      },
    },
    {
      disabled: false,
      error: false,
      css: {
        boxShadowColor: {
          _focusVisible: 'border.action.hero.selected.default',
        },
      },
    },
    {
      readOnly: false,
      disabled: false,
      hasContext: false,
      css: {
        backgroundColor: {
          _hover: 'DateInput.bg.hover',
          _active: 'bg.neutral',
          _focusVisible: 'bg.neutral',
        },
        boxShadowColor: {
          _hover: 'DateInput.border.hover',
          _active: 'border.action.hero.selected.default',
        },
      },
    },
    {
      readOnly: true,
      disabled: false,
      css: {
        backgroundColor: {
          base: 'bg.invisible',
          _active: 'bg.invisible',
          _focusVisible: 'bg.invisible',
          _hover: 'bg.invisible',
        },
        boxShadowColor: {
          base: 'border.invisible',
          _active: 'border.invisible',
          _focusVisible: 'border.invisible',
          _hover: 'border.invisible',
        },
      },
    },
    {
      error: true,
      disabled: false,
      hasContext: false,
      readOnly: false,
      css: {
        'backgroundColor': {
          base: 'DateInput.bg.error',
          _hover: 'DateInput.bg.errorHover',
          _active: 'bg.neutral',
          _focusVisible: 'bg.neutral',
        },
        'boxShadowColor': {
          base: 'DateInput.border.error',
          _hover: 'DateInput.border.errorHover',
        },
        '&.edge-autofilled, &:-webkit-autofill, &:autofill': {
          // Override user agent stylesheet autofill background
          // This also preserves the border color
          boxShadow: {
            _hover:
              'inset 0 0 0 {borderWidths.Input.default} var(--shadow-color), inset 0 0 0 1000px {colors.Input.bg.errorHover}',
            _focus:
              'inset 0 0 0 {borderWidths.Input.default} var(--shadow-color), {shadows.focus}, inset 0 0 0 1000px {colors.Input.bg.errorActive}',
            _active:
              'inset 0 0 0 {borderWidths.Input.default} var(--shadow-color), inset 0 0 0 1000px {colors.Input.bg.errorActive}',
          },
        },
      },
    },
    {
      error: true,
      disabled: false,
      css: {
        boxShadowColor: {
          _hover: 'DateInput.border.errorHover',
          _active: 'DateInput.border.errorActive',
          _focusVisible: 'DateInput.border.errorActive',
        },
      },
    },
    {
      size: 'sm',
      readOnly: true,
      css: {
        paddingInline: 'none',
      },
    },
    {
      size: 'md',
      readOnly: true,
      css: {
        paddingInline: 'none',
      },
    },
    {
      size: 'lg',
      readOnly: true,
      css: {
        paddingInline: 'none',
      },
    },
  ],
});
